import { graphql } from 'gatsby';

import { styled } from '@mui/material';

import { ResponsiveVideo } from '@mewa/ui';

import { imagePopulate } from '../../../../../libs/strapi-populates';
import {
  ComponentProps,
  PossibleContentSections,
} from '../../fragment-and-types';
import { ContentLivePreviewConfig } from '../../live-preview/types';
import { StrapiImage } from '../image';
import {
  componentName,
  ResponsiveVideoData,
  ResponsiveVideoPreview,
  StrapiVideoFile,
} from './types';

export const ResponsiveVideoFragment = graphql`
  fragment ResponsiveVideoFragment on STRAPI__COMPONENT_SHARED_RESPONSIVE_VIDEO {
    videoDesktop: VideoDesktop {
      fields {
        assetUrl
      }
    }
    videoMobile: VideoMobile {
      fields {
        assetUrl
      }
    }
    posterImage: PosterImage {
      ...StrapiImageFragment
    }
    autoPlay: AutoPlay
    strapi_component
  }
`;

export const isResponsiveVideo = (
  component: PossibleContentSections,
): component is ResponsiveVideoData => {
  return component.strapi_component === componentName;
};

export const StrapiResponsiveVideo = ({
  autoPlay,
  videoDesktop,
  videoMobile,
  posterImage,
}: ComponentProps<ResponsiveVideoData>) => {
  const posterImageComponent = posterImage ? (
    <StrapiImage responsiveImage={posterImage} />
  ) : null;

  return (
    <Wrapper>
      <ResponsiveVideo
        autoPlay={autoPlay}
        desktopSources={gatsbyVideoDataFromStrapiVideo(videoDesktop)}
        mobileSources={gatsbyVideoDataFromStrapiVideo(videoMobile)}
        posterImage={posterImageComponent}
      />
    </Wrapper>
  );
};

const Wrapper = styled('div')(({ theme }) => ({
  marginBottom: '40px',

  [theme.breakpoints.up('sm')]: {
    marginBottom: '100px',
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: '150px',
  },
}));

export const gatsbyVideoDataFromStrapiVideo = (video: StrapiVideoFile) =>
  video ? [{ src: video.fields.assetUrl, type: 'video/mp4' }] : [];

export const config = {
  isComponent: isResponsiveVideo,
  Component: StrapiResponsiveVideo,
  componentName,
};

export const populationParams = {
  VideoDesktop: '*',
  VideoMobile: '*',
  PosterImage: imagePopulate,
};

export const previewConfig: ContentLivePreviewConfig = {
  populationParams,
  componentName,
  mapProperties: (responsiveVideo): ResponsiveVideoData | null =>
    responsiveVideo.strapi_component === componentName
      ? {
          ...responsiveVideo,
          ...mapVideoAttributes(responsiveVideo),
        }
      : null,
};

export const mapVideoAttributes = (
  data: ResponsiveVideoPreview,
): ResponsiveVideoData => ({
  videoDesktop: data.VideoDesktop,
  videoMobile: data.VideoMobile,
  posterImage: data.PosterImage,
  autoPlay: data.AutoPlay,
  strapi_component: componentName,
});
